import React from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';
import { ManagerCommentCell } from '~/components/CommentCell/ManagerCommentCell.tsx';
import { FormAutocomplete } from '~/components/FormComponents/FormAutocomplete.tsx';
import { FormInput } from '~/components/FormComponents/FormInput.tsx';
import { dataFormStyles } from '~/modules/covenants/CovenantsDataForm/styles.ts';
import { guaranteesClientPageStyles } from '~/modules/guarantees/form/client/styles.ts';
import ButtonLoadingIcon from '~/modules/guarantees/form/shared/ButtonLoadingIcon.tsx';
import {
  GuaranteeSecurityType,
  type TGuaranteesExecutionForm,
} from '~/store/guarantees/types.ts';
import FlowSection from '../../components/root/shared/FlowSection.tsx';
import {
  ACCOUNT_PLACEMENT_STATUS_OPTIONS,
  EXECUTOR_MAIN_DEAL_CONDITIONS_STATUS_OPTIONS,
  UAKDO_CONTROL_TEXT,
} from '../step7/constants.ts';
import { UAKDO_CONTROLLER_CONTROL_TEXT } from './constants.ts';
import { uakdoControllerControlSectionStyles } from './styles.ts';
import {
  GuaranteesUakdoControllerControlFieldName,
  type TGuaranteesUakdoControllerControlFormValues,
} from './types.ts';
import { getGuaranteesUakdoControllerControlInitialValues } from './utils.ts';

type TUakdoControllerControlSectionProps = {
  executionForm: TGuaranteesExecutionForm | null;
  isConfirming: boolean;
  isDisabled?: boolean;
  isLoading: boolean;
  isRejecting: boolean;
  securityType: GuaranteeSecurityType | '';
  onConfirm: () => Promise<void>;
  onReject: (comment: string) => Promise<void>;
};

const UakdoControllerControlSection: React.FC<TUakdoControllerControlSectionProps> = ({
  executionForm,
  isConfirming,
  isDisabled = false,
  isLoading,
  isRejecting,
  securityType,
  onConfirm,
  onReject,
}) => {
  const defaultValues = React.useMemo(
    () => getGuaranteesUakdoControllerControlInitialValues(executionForm),
    [executionForm]
  );
  const formMethods = useForm<TGuaranteesUakdoControllerControlFormValues>({
    defaultValues,
  });
  const { handleSubmit, reset } = formMethods;
  const [isRejectCommentOpen, setIsRejectCommentOpen] = React.useState(false);
  const areActionsDisabled = isConfirming || isRejecting || isDisabled;
  const confirmStartIcon = isConfirming ? <ButtonLoadingIcon /> : undefined;
  const rejectStartIcon = isRejecting ? <ButtonLoadingIcon /> : undefined;
  const showCoverageStatus =
    securityType === GuaranteeSecurityType.COVERAGE_ACCOUNT ||
    !!executionForm?.coverageAccountPlacementStatus;
  const showDepositStatus =
    securityType === GuaranteeSecurityType.DEPOSIT_RIGHTS ||
    !!executionForm?.depositAccountPlacementStatus;

  React.useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const handleConfirm: SubmitHandler<TGuaranteesUakdoControllerControlFormValues> = async () => {
    try {
      await onConfirm();
    } catch {
      // Request feedback is handled by the page-level callback.
    }
  };

  const handleReject: SubmitHandler<TGuaranteesUakdoControllerControlFormValues> = async (
    _values
  ) => {
    setIsRejectCommentOpen(true);
  };

  const handleSaveRejectComment = async (comment: string) => {
    await onReject(comment);
  };

  if (isLoading) {
    return (
      <FlowSection
        step={UAKDO_CONTROLLER_CONTROL_TEXT.sectionStepLabel}
        title={UAKDO_CONTROLLER_CONTROL_TEXT.sectionTitle}
        description={UAKDO_CONTROLLER_CONTROL_TEXT.sectionDescription}
      >
        <Box sx={guaranteesClientPageStyles.flowLoadingState}>
          <CircularProgress size={24} />
          <Typography sx={guaranteesClientPageStyles.flowLockHint}>
            {UAKDO_CONTROLLER_CONTROL_TEXT.loadingText}
          </Typography>
        </Box>
      </FlowSection>
    );
  }

  return (
    <FormProvider {...formMethods}>
      <FlowSection
        step={UAKDO_CONTROLLER_CONTROL_TEXT.sectionStepLabel}
        title={UAKDO_CONTROLLER_CONTROL_TEXT.sectionTitle}
        description={UAKDO_CONTROLLER_CONTROL_TEXT.sectionDescription}
      >
        <Box sx={uakdoControllerControlSectionStyles.fieldsStack}>
          <Box sx={uakdoControllerControlSectionStyles.topRow}>
            <FormAutocomplete
              name={GuaranteesUakdoControllerControlFieldName.ExecutorMainDealConditionsStatus}
              label={UAKDO_CONTROL_TEXT.executorMainDealConditionsStatusLabel}
              placeholder={UAKDO_CONTROL_TEXT.executorMainDealConditionsStatusPlaceholder}
              options={EXECUTOR_MAIN_DEAL_CONDITIONS_STATUS_OPTIONS}
              disabled
              sx={uakdoControllerControlSectionStyles.readOnlyField}
            />
            {showCoverageStatus && (
              <FormAutocomplete
                name={GuaranteesUakdoControllerControlFieldName.CoverageAccountPlacementStatus}
                label={UAKDO_CONTROL_TEXT.coverageAccountPlacementStatusLabel}
                placeholder={UAKDO_CONTROL_TEXT.coverageAccountPlacementStatusPlaceholder}
                options={ACCOUNT_PLACEMENT_STATUS_OPTIONS}
                disabled
                sx={uakdoControllerControlSectionStyles.readOnlyField}
              />
            )}
            {showDepositStatus && (
              <FormAutocomplete
                name={GuaranteesUakdoControllerControlFieldName.DepositAccountPlacementStatus}
                label={UAKDO_CONTROL_TEXT.depositAccountPlacementStatusLabel}
                placeholder={UAKDO_CONTROL_TEXT.depositAccountPlacementStatusPlaceholder}
                options={ACCOUNT_PLACEMENT_STATUS_OPTIONS}
                disabled
                sx={uakdoControllerControlSectionStyles.readOnlyField}
              />
            )}
          </Box>
          <FormInput
            name={GuaranteesUakdoControllerControlFieldName.UnfulfilledConditionsDetails}
            label={UAKDO_CONTROL_TEXT.unfulfilledConditionsDetailsLabel}
            inputProps={{ readOnly: true }}
            multiline
            minRows={3}
            sx={[
              uakdoControllerControlSectionStyles.fullWidthField,
              uakdoControllerControlSectionStyles.readOnlyField,
            ]}
          />
        </Box>
      </FlowSection>

      <Box sx={guaranteesClientPageStyles.flowPageActionsCentered}>
        <Button
          type="button"
          variant="outlined"
          disabled={areActionsDisabled}
          onClick={() => void handleSubmit(handleReject)()}
          startIcon={rejectStartIcon}
          sx={uakdoControllerControlSectionStyles.rejectButton}
        >
          {UAKDO_CONTROLLER_CONTROL_TEXT.rejectButton}
        </Button>
        <Button
          type="button"
          variant="contained"
          disabled={areActionsDisabled}
          onClick={() => void handleSubmit(handleConfirm)()}
          startIcon={confirmStartIcon}
          sx={dataFormStyles.documentPrimaryAction}
        >
          {UAKDO_CONTROLLER_CONTROL_TEXT.confirmButton}
        </Button>
      </Box>

      <Box sx={{ display: 'none' }}>
        <ManagerCommentCell
          initialComment=""
          open={isRejectCommentOpen}
          onOpenChange={setIsRejectCommentOpen}
          onSave={handleSaveRejectComment}
          labels={{
            dialogTitle: UAKDO_CONTROLLER_CONTROL_TEXT.rejectCommentTitle,
            save: UAKDO_CONTROLLER_CONTROL_TEXT.rejectCommentConfirmButton,
            saving: UAKDO_CONTROLLER_CONTROL_TEXT.rejectCommentLoadingButton,
          }}
        />
      </Box>
    </FormProvider>
  );
};

export default UakdoControllerControlSection;
